import { Hand, Inbox, MoonStar } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Badge, Button, EmptyState } from '@/components/ui'
import { formatDate, translateEnumValue } from '@/lib/formatting'
import { cn } from '@/lib/utils'

import { formatWaitingTime } from './ticketFormatting'
import type { DeskConversationFilter, DeskConversationSummary } from './types'

const FILTERS: readonly DeskConversationFilter[] = ['open', 'pending', 'resolved', 'all']

function statusVariant(status: string): 'success' | 'warning' | 'neutral' {
  const normalized = status.toLowerCase()
  if (normalized === 'open') {
    return 'success'
  }

  if (normalized === 'pending') {
    return 'warning'
  }

  return 'neutral'
}

interface ConversationListProps {
  readonly conversations: readonly DeskConversationSummary[]
  readonly selectedId: number | null
  readonly onSelect: (id: number) => void
  readonly filter: DeskConversationFilter
  readonly onFilterChange: (filter: DeskConversationFilter) => void
  readonly onReload: () => void
  readonly language: string
}

export function ConversationList({
  conversations,
  selectedId,
  onSelect,
  filter,
  onFilterChange,
  onReload,
  language,
}: ConversationListProps) {
  const { t } = useTranslation()

  const statusLabels: Readonly<Record<string, string>> = {
    open: t('desk.statuses.open'),
    pending: t('desk.statuses.pending'),
    resolved: t('desk.statuses.resolved'),
  }

  return (
    <div className="flex h-full flex-col">
      <div
        role="tablist"
        aria-label={t('desk.filters.label')}
        className="flex shrink-0 gap-1 border-b border-border-default p-2"
      >
        {FILTERS.map((value) => (
          <button
            key={value}
            type="button"
            role="tab"
            aria-selected={filter === value}
            onClick={() => {
              onFilterChange(value)
            }}
            className={cn(
              'rounded-md px-2.5 py-1 text-xs font-medium',
              filter === value
                ? 'bg-background-subtle text-foreground-primary'
                : 'text-foreground-muted hover:text-foreground-secondary',
            )}
          >
            {t(`desk.filters.${value}`)}
          </button>
        ))}
      </div>

      {conversations.length === 0 ? (
        <div className="p-4">
          <EmptyState
            icon={Inbox}
            title={t('desk.list.emptyTitle')}
            description={t('desk.list.emptyDescription')}
            action={
              <Button variant="secondary" size="sm" onClick={onReload}>
                {t('desk.list.reload')}
              </Button>
            }
          />
        </div>
      ) : (
        <ul className="flex-1 divide-y divide-border-default overflow-y-auto">
          {conversations.map((conversation) => {
            const isSelected = conversation.id === selectedId
            return (
              <li key={conversation.id}>
                <button
                  type="button"
                  aria-current={isSelected}
                  onClick={() => {
                    onSelect(conversation.id)
                  }}
                  className={cn(
                    'flex w-full flex-col gap-1 px-4 py-3 text-left hover:bg-background-subtle',
                    isSelected && 'bg-background-subtle',
                  )}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="truncate text-sm font-medium text-foreground-primary">
                      {conversation.contactName ?? t('desk.list.unknownContact')}
                    </span>
                    <Badge variant={statusVariant(conversation.status)}>
                      {translateEnumValue(statusLabels, conversation.status.toLowerCase())}
                    </Badge>
                  </div>
                  {conversation.ticket !== null ? (
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-status-warning">
                      <Hand aria-hidden="true" className="h-3.5 w-3.5" />
                      {conversation.ticket.status === 'waiting'
                        ? t('desk.ticket.listWaiting', {
                            number: conversation.ticket.number,
                            time: formatWaitingTime(conversation.ticket.createdAt, t),
                          })
                        : t('desk.ticket.listInProgress', { number: conversation.ticket.number })}
                      {conversation.ticket.openedWhileUnavailable ? (
                        <MoonStar
                          aria-label={t('desk.ticket.openedOffline')}
                          className="h-3.5 w-3.5 text-foreground-muted"
                        />
                      ) : null}
                    </span>
                  ) : null}
                  {conversation.lastMessagePreview !== null ? (
                    <span className="truncate text-xs text-foreground-muted">
                      {conversation.lastMessagePreview}
                    </span>
                  ) : null}
                  {conversation.lastActivityAt !== null ? (
                    <span className="text-[11px] text-foreground-muted">
                      {formatDate(conversation.lastActivityAt, language)}
                    </span>
                  ) : null}
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
